/**
 * Iter 101 — Admin Daily Payments & Commissions panel (billing tab top).
 * Range filters, financial summary cards, alerts, transaction feed,
 * per-vendor breakdown, CSV exports and manual vendor-payment recording.
 */
import { useCallback, useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Money from "@/components/Money";
import { Input } from "@/components/ui/input";
import { Download, Plus, AlertTriangle } from "lucide-react";

const RANGES = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["month", "This Month"], ["custom", "Custom"]];
const TYPE_LABEL = { client_commission: "Client Commission", refund: "Refund", vendor_payment: "Vendor Payment", manual_payment: "Vendor Payment" };
const STATUS_CLS = { paid: "bg-emerald-50 text-emerald-700", pending: "bg-amber-50 text-amber-700", refunded: "bg-rose-50 text-rose-700", failed: "bg-rose-100 text-rose-800", overdue: "bg-rose-50 text-rose-700" };

function qs(range, from, to, extra = "") {
  return `range=${range}${range === "custom" ? `&date_from=${from}&date_to=${to}` : ""}${extra}`;
}

export default function AdminFinancePanel({ vendors = [] }) {
  const [range, setRange] = useState("today");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [summary, setSummary] = useState(null);
  const [sub, setSub] = useState("transactions");
  const [tx, setTx] = useState(null);
  const [vb, setVb] = useState(null);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [recOpen, setRecOpen] = useState(false);
  const [rec, setRec] = useState({ vendor_id: "", amount: "", reason: "", method: "bank transfer" });

  const ready = range !== "custom" || (from && to);

  const load = useCallback(() => {
    if (!ready) return;
    api.get(`/admin/finance/summary?${qs(range, from, to)}`).then((r) => setSummary(r.data)).catch(() => {});
    api.get(`/admin/finance/transactions?${qs(range, from, to, `${type ? `&type=${type}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}&limit=50`)}`)
      .then((r) => setTx(r.data)).catch(() => {});
    api.get(`/admin/finance/vendors?${qs(range, from, to)}`).then((r) => setVb(r.data)).catch(() => {});
  }, [range, from, to, type, q, ready]);

  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);

  const cards = summary ? [
    ["Client Commissions", summary.client_commissions, "text-emerald-700"],
    ["Vendor Payments", summary.vendor_payments, "text-blue-700"],
    ["Refunds", summary.refunds, "text-rose-600"],
    ["Net Platform Revenue", summary.net_revenue, "text-neutral-900"],
    ["Total Rental Value", summary.total_rental_value, "text-neutral-500"],
    ["Pending Vendor Payments", summary.pending_vendor_payments, "text-amber-600"],
  ] : [];

  const submitRec = async () => {
    try {
      await api.post("/admin/finance/vendor-payments", { ...rec, amount: Number(rec.amount) });
      toast.success("Vendor payment recorded");
      setRecOpen(false);
      setRec({ vendor_id: "", amount: "", reason: "", method: "bank transfer" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mb-10 rounded-3xl border border-border bg-white p-4 sm:p-5" data-testid="admin-finance-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h2 className="font-display text-xl font-bold">Payments & Commissions</h2>
        <div className="flex gap-2">
          <a href={`${API}/admin/finance/export.csv?report=transactions&${qs(range, from, to)}`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-bold" data-testid="export-transactions-csv">
            <Download className="h-3.5 w-3.5" /> Transactions CSV
          </a>
          <a href={`${API}/admin/finance/export.csv?report=vendors&${qs(range, from, to)}`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-bold" data-testid="export-vendors-csv">
            <Download className="h-3.5 w-3.5" /> Vendor CSV
          </a>
        </div>
      </div>

      <div className="flex gap-1.5 flex-wrap mb-3" data-testid="finance-range-filters">
        {RANGES.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setRange(k)} className={`px-3.5 py-1.5 rounded-full text-xs font-bold border ${range === k ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-border text-neutral-600"}`} data-testid={`finance-range-${k}`}>
            {label}
          </button>
        ))}
        {range === "custom" && (
          <div className="flex gap-1.5">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-full border border-border px-3 py-1 text-xs" data-testid="finance-date-from" />
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-full border border-border px-3 py-1 text-xs" data-testid="finance-date-to" />
          </div>
        )}
      </div>

      {summary && (summary.alerts.overdue_vendor_payments > 0 || summary.alerts.uncollected_commissions > 0) && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-3 mb-3 text-sm font-semibold text-amber-800 space-y-0.5" data-testid="finance-alerts">
          {summary.alerts.overdue_vendor_payments > 0 && <div><AlertTriangle className="h-3.5 w-3.5 inline mr-1" />{summary.alerts.overdue_vendor_payments} vendor payment{summary.alerts.overdue_vendor_payments !== 1 ? "s" : ""} overdue (14+ days)</div>}
          {summary.alerts.uncollected_commissions > 0 && <div><AlertTriangle className="h-3.5 w-3.5 inline mr-1" />{summary.alerts.uncollected_commissions} reservation{summary.alerts.uncollected_commissions !== 1 ? "s" : ""} with no commission collected</div>}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
        {cards.map(([label, val, cls]) => (
          <div key={label} className="rounded-2xl border border-border p-3" data-testid={`finance-card-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className={`text-lg font-display font-bold tabular-nums ${cls}`}><Money amount={val} /></div>
            <div className="text-[10px] text-neutral-500 font-bold uppercase tracking-wide mt-0.5 leading-tight">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        {[["transactions", "Transactions"], ["vendors", "By Vendor"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setSub(k)} className={`px-3.5 py-1.5 rounded-full text-xs font-bold ${sub === k ? "bg-blue-600 text-white" : "bg-neutral-100 text-neutral-600"}`} data-testid={`finance-sub-${k}`}>
            {label}
          </button>
        ))}
        <button type="button" onClick={() => setRecOpen(!recOpen)} className="inline-flex items-center gap-1 px-3.5 py-1.5 rounded-full text-xs font-bold bg-emerald-600 text-white" data-testid="record-vendor-payment-toggle">
          <Plus className="h-3 w-3" /> Record vendor payment
        </button>
      </div>

      {recOpen && (
        <div className="rounded-2xl border border-border p-3 mb-3 grid grid-cols-1 sm:grid-cols-5 gap-2" data-testid="record-vendor-payment-form">
          <select value={rec.vendor_id} onChange={(e) => setRec({ ...rec, vendor_id: e.target.value })} className="rounded-xl border border-border px-2 py-2 text-sm" data-testid="rec-vendor">
            <option value="">Vendor…</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.business_name || v.name || v.email}</option>)}
          </select>
          <Input type="number" placeholder="Amount €" value={rec.amount} onChange={(e) => setRec({ ...rec, amount: e.target.value })} className="rounded-xl" data-testid="rec-amount" />
          <Input placeholder="Reason (commission, subscription…)" value={rec.reason} onChange={(e) => setRec({ ...rec, reason: e.target.value })} className="rounded-xl sm:col-span-2" data-testid="rec-reason" />
          <button type="button" onClick={submitRec} disabled={!rec.vendor_id || !rec.amount || !rec.reason} className="rounded-xl bg-neutral-900 text-white text-sm font-bold disabled:opacity-50" data-testid="rec-submit">Save</button>
        </div>
      )}

      {sub === "transactions" && (
        <>
          <div className="flex gap-2 flex-wrap mb-3">
            <Input placeholder="Search reservation, vendor, customer, transaction ID…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-xl max-w-md" data-testid="finance-search" />
            <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-xl border border-border px-2 text-sm" data-testid="finance-type-filter">
              <option value="">All types</option>
              <option value="client_commission">Client Commission</option>
              <option value="vendor_payment">Vendor Payment</option>
              <option value="refund">Refund</option>
            </select>
          </div>
          <div className="space-y-2" data-testid="finance-transactions">
            {(tx?.items || []).map((r) => (
              <div key={r.id} className="rounded-2xl border border-border p-3 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`finance-tx-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md bg-neutral-100 text-neutral-600">{TYPE_LABEL[r.type] || "Vendor Payment"}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-md ${STATUS_CLS[r.status] || "bg-neutral-100 text-neutral-600"}`}>{r.status}</span>
                    {r.reservation && <span className="font-mono text-[11px] text-neutral-400 font-bold">{r.reservation}</span>}
                  </div>
                  <div className="text-sm font-semibold mt-0.5 truncate">
                    {r.vendor}{r.customer ? ` · ${r.customer}` : ""}{r.reason ? ` · ${r.reason}` : ""}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {String(r.at || "").slice(0, 16).replace("T", " ")} · {r.method}
                    {r.rental_total != null && <> · Rental <Money amount={r.rental_total} />{r.commission_pct != null && ` · ${r.commission_pct}%`} · Vendor balance <Money amount={r.vendor_balance || 0} /></>}
                    {r.reference && <span className="font-mono"> · {String(r.reference).slice(0, 24)}</span>}
                  </div>
                </div>
                <div className={`text-lg font-display font-bold tabular-nums ${r.amount < 0 ? "text-rose-600" : "text-emerald-700"}`}><Money amount={r.amount} /></div>
              </div>
            ))}
            {tx && tx.items.length === 0 && <div className="text-sm text-neutral-400 text-center py-8" data-testid="finance-tx-empty">No transactions in this range.</div>}
          </div>
        </>
      )}

      {sub === "vendors" && (
        <div className="space-y-2" data-testid="finance-vendor-breakdown">
          {(vb?.items || []).map((v) => (
            <div key={v.vendor_id || v.vendor} className="rounded-2xl border border-border p-3 flex flex-wrap items-center gap-x-5 gap-y-1" data-testid={`finance-vendor-${v.vendor_id}`}>
              <div className="font-bold text-sm flex-1 min-w-[120px]">{v.vendor}</div>
              <div className="text-xs text-neutral-500">Reservations <span className="font-bold text-neutral-900">{v.reservations}</span></div>
              <div className="text-xs text-neutral-500">Rental <span className="font-bold text-neutral-900"><Money amount={v.rental_value} /></span></div>
              <div className="text-xs text-neutral-500">Commissions <span className="font-bold text-emerald-700"><Money amount={v.client_commissions} /></span></div>
              <div className="text-xs text-neutral-500">Payments <span className="font-bold text-blue-700"><Money amount={v.vendor_payments} /></span></div>
              <div className="text-xs text-neutral-500">Outstanding <span className={`font-bold ${v.outstanding > 0 ? "text-amber-600" : "text-neutral-900"}`}><Money amount={v.outstanding} /></span></div>
            </div>
          ))}
          {vb && vb.items.length === 0 && <div className="text-sm text-neutral-400 text-center py-8">No vendor activity in this range.</div>}
        </div>
      )}
    </div>
  );
}
